import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowRight, ArrowUpRight, CalendarDays, Clock, MapPin, Users } from "lucide-react";
import { Reveal, MaskedLine } from "../components/site/Reveal";
import { Seo } from "../components/site/Seo";
import { DPS_MEDIA } from "../lib/content";
import { API, fmtEventDate, fmtEventTime, fmtPrice } from "../lib/eventUtils";
import { trackEvent } from "../lib/analytics";

const EVENTS = [
  {
    meta: "FRI 6:00 PM · SAT 11:30 AM · $20",
    title: "HYROX Specialty Classes",
    when: "Dallas · 90-minute coached sessions",
    body: "Exercises designed to enhance HYROX performance by targeting muscle endurance, strength, fatigue, heart rate and compromised running ability. Follows the 12-week periodized program.",
    cta: { to: "/schedule", label: "Class Details", track: "classes" },
  },
  {
    meta: "SEASONAL · $25",
    title: "HYROX Full Simulation",
    when: "Normally starts 11:30 AM – 3:00 PM",
    body: "A practice session that replicates the entire race from start to finish — all 8 km of running and all 8 functional stations, in the exact order, with the official weights and reps for your division.",
    cta: { to: "/simulation-format", label: "How Sim Day Runs" },
  },
  {
    meta: "NOV 15 · WEEKEND BEFORE RACE",
    title: "Shake Out Run",
    when: "Hyrox Ninja × HYROX Dallas",
    body: "A light community shake-out run the weekend prior to HYROX Dallas — loosen up, meet your fellow racers and lock in your race-week routine.",
  },
  {
    meta: "TBA · AUG 28 – NOV 18 WINDOW",
    title: "Recovery Sessions",
    when: "Dates being scheduled",
    body: "Guided recovery sessions placed between the start of the 12-week program and race week. Dates will be announced — watch this page for open registrations.",
  },
  {
    meta: "TBA",
    title: "Lululemon Gatherings",
    when: "Lululemon Dallas locations",
    body: "Private shopping events for our HYROX family and private recovery sessions hosted at Lululemon locations. Details announced soon.",
  },
];

export default function Events() {
  const [liveEvents, setLiveEvents] = useState([]);
  useEffect(() => {
    axios.get(`${API}/api/events`).then((r) => setLiveEvents(r.data)).catch(() => {});
  }, []);

  return (
    <div data-testid="events-page" className="pt-16">
      <Seo
        title="HYROX Dallas Events — Hyrox Ninja × Dallas Performance Systems | HyroxNinja"
        description="Every HYROX event on the Hyrox Ninja calendar: Dallas Performance Systems Sunday workouts with on-site signup, specialty classes, full simulations and community runs. One Dallas HYROX calendar."
      />
      <section className="relative overflow-hidden">
        <img src={DPS_MEDIA.bg.events} alt="" aria-hidden="true" className="absolute inset-0 w-full h-full object-cover opacity-15" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#070707]/70 via-[#070707]/40 to-[#070707]" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-12">
        <p className="eyebrow mb-4">The HyroxNinja Event Directory</p>
        <h1 className="font-head font-black uppercase tracking-tighter leading-[0.9] text-4xl sm:text-5xl lg:text-6xl mb-6">
          <MaskedLine delay={0.1}>Every Event</MaskedLine>
          <MaskedLine delay={0.25} className="text-volt">On the Calendar</MaskedLine>
        </h1>
        <p className="text-zinc-400 max-w-2xl leading-relaxed">
          One Dallas HYROX calendar, built with{" "}
          <span className="text-white font-semibold">Dallas Performance Systems</span>. Weekly sessions, full simulations,
          community runs and partner workouts — sign up right here on HyroxNinja.
        </p>
        </div>
      </section>

      {/* LIVE EVENTS — ON-SITE SIGNUP */}
      {liveEvents.length > 0 && (
        <section id="open-registration" className="max-w-7xl mx-auto px-5 lg:px-8 pb-16 scroll-mt-24" data-testid="live-events-section">
          <Reveal>
            <p className="eyebrow mb-3">Open for Registration</p>
            <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-8">
              Sign up right here
            </h2>
          </Reveal>
          <div className={`grid gap-px bg-white/10 border border-volt/40 ${liveEvents.length > 1 ? "lg:grid-cols-2" : ""}`}>
            {liveEvents.map((ev, i) => (
              <Reveal key={ev.slug} delay={i * 0.05} className="h-full">
                <article className="relative bg-[#0c0c0c] h-full overflow-hidden group" data-testid={`live-event-card-${ev.slug}`}>
                  {ev.backdrop_poster && (
                    <img src={ev.backdrop_poster} alt="" className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-30 transition-opacity" />
                  )}
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0c0c0c] via-[#0c0c0c]/70 to-transparent" />
                  <div className="relative p-7 sm:p-8 flex flex-col h-full">
                    <span className="inline-flex w-fit font-mono2 text-[10px] uppercase tracking-[0.25em] border border-volt/60 text-volt px-2.5 py-1 mb-4">
                      {ev.partner}
                    </span>
                    <h3 className="font-head font-extrabold uppercase text-2xl sm:text-3xl tracking-tight mb-3">{ev.title}</h3>
                    <p className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-zinc-400 flex flex-wrap gap-x-5 gap-y-1.5 mb-5">
                      <span className="flex items-center gap-1.5"><CalendarDays size={12} className="text-volt" /> {fmtEventDate(ev.date_start)}</span>
                      <span className="flex items-center gap-1.5"><Clock size={12} className="text-volt" /> {fmtEventTime(ev.date_start)}</span>
                      <span className="flex items-center gap-1.5"><MapPin size={12} className="text-volt" /> {ev.location_name}</span>
                      {ev.price_cents > 0 && (
                        <span className="text-volt font-bold" data-testid={`live-event-price-${ev.slug}`}>{fmtPrice(ev.price_cents)}</span>
                      )}
                    </p>
                    <div className="mt-auto flex flex-wrap items-center justify-between gap-4">
                      <span className={`font-mono2 text-[11px] uppercase tracking-[0.2em] flex items-center gap-2 ${ev.spots_remaining > 0 ? "text-volt" : "text-red-400"}`} data-testid={`live-event-spots-${ev.slug}`}>
                        <Users size={13} /> {ev.spots_remaining > 0 ? `${ev.spots_remaining} of ${ev.capacity} spots left` : "Sold out"}
                      </span>
                      <Link to={`/events/${ev.slug}`} className="btn-volt !py-2.5 !px-5 !text-sm" data-testid={`live-event-cta-${ev.slug}`} onClick={() => trackEvent("event_card_click", { event: ev.slug })}>
                        View & Sign Up <ArrowRight size={15} />
                      </Link>
                    </div>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        </section>
      )}

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-16" data-testid="events-list-section">
        <Reveal>
          <p className="eyebrow mb-3">The Hyrox Ninja Program</p>
          <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-8">
            Weekly classes, sims & runs
          </h2>
        </Reveal>
        <div className="grid lg:grid-cols-2 gap-px bg-white/10 border border-white/10">
          {EVENTS.map((e, i) => (
            <Reveal key={e.title} delay={(i % 2) * 0.05} className="h-full">
              <article className="bg-[#0c0c0c] p-7 sm:p-8 h-full flex flex-col" data-testid={`event-card-${i}`}>
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className="font-mono2 text-[9px] uppercase tracking-[0.2em] border border-white/20 text-zinc-400 px-2 py-0.5">Hyrox Ninja</span>
                  <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-volt">{e.meta}</p>
                </div>
                <h2 className="font-head font-bold uppercase text-2xl mb-1">{e.title}</h2>
                <p className="font-mono2 text-[11px] uppercase tracking-[0.15em] text-zinc-500 mb-3 flex items-center gap-2">
                  <CalendarDays size={13} className="shrink-0" /> {e.when}
                </p>
                <p className="text-zinc-400 text-sm leading-relaxed mb-5">{e.body}</p>
                {e.cta && (
                  <Link
                    to={e.cta.to}
                    className="inline-flex items-center gap-2 text-volt font-mono2 text-xs uppercase tracking-[0.2em] hover:gap-3 transition-all mt-auto w-fit"
                    data-testid={`event-cta-${i}`}
                    onClick={() => e.cta.track && trackEvent("booking_click", { placement: "events_page", service: e.cta.track })}
                  >
                    {e.cta.label} <ArrowRight size={14} />
                  </Link>
                )}
              </article>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24">
        <Reveal>
          <div className="border border-volt/40 bg-volt/5 p-8 lg:p-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-6">
            <div>
              <h2 className="font-head font-extrabold uppercase tracking-tight text-2xl sm:text-3xl mb-2">Ready to lock in a spot?</h2>
              <p className="text-zinc-400 max-w-xl leading-relaxed">Events and simulations are booked right here on HyroxNinja with secure Stripe checkout.</p>
            </div>
            <div className="flex flex-wrap gap-3 shrink-0">
              <a href="#open-registration" className="btn-volt" data-testid="events-book-button" onClick={() => trackEvent("booking_click", { placement: "events_page", service: "general" })}>
                View Open Registrations <ArrowRight size={16} />
              </a>
              <Link to="/schedule" className="btn-ghost" data-testid="events-schedule-link">
                Weekly Schedule <ArrowUpRight size={15} />
              </Link>
            </div>
          </div>
        </Reveal>
        <Reveal>
          <p className="text-zinc-500 text-xs leading-relaxed mt-6" data-testid="events-blog-links">
            Deciding what to book? See{" "}
            <Link to="/blog/hyrox-simulation-near-me-dallas" className="text-volt hover:text-white transition-colors underline underline-offset-2">where to find a HYROX simulation near you</Link>
            {" "}and{" "}
            <Link to="/blog/dps-sunday-hyrox-workout" className="text-volt hover:text-white transition-colors underline underline-offset-2">what to expect at the DPS Sunday workout</Link>.
          </p>
        </Reveal>
      </section>
    </div>
  );
}
